<template>
  <div>
    <TipTapReader
      v-if="isBookingUiV2 && !loading && desc_options.content && !skipPromotionIntro"
      class="tiptap_style pa-0"
      :options="desc_options"
      :key="descComponentKey + 'des-v2'"
      style="min-height: 0px"
    />
    <BookingUiV2
      v-if="isBookingUiV2"
      :loading="loading"
      :lang="lang"
      :concepts="selectableConceptList"
      :selected-concept="selected_concept"
      :concept-locked="!!(promotionPriceInfo && promotionPriceInfo.conceptId)"
      :product-type="productType"
      :show-staff="showStaffSelectionArea"
      :staff-list="staffChoicesForSelectedTime"
      :selected-staff-id="selectedStaffId"
      :staff-loading="staffLoading"
      :date="date"
      :available-times="available_time_list"
      :reserved-times="already_reserved_time_list"
      :selected-time="selected_time"
      :time-loading="timeLoading"
      :staff-holiday-info="staffHolidayInfo"
      :is-past-date="isPastSelectedDate"
      :is-same-day-blocked="isSameDayReservationBlocked"
      :is-outside-promotion-period="isOutsidePromotionPeriod"
      :products="isBookingUiV2LocalSafetyPreview ? [] : sortedProducts"
      :selected-product="selectedProduct"
      :persons="persons"
      :option-products="filteredOptionProducts"
      :selected-option-products="selectedOptionProducts"
      :total-price="computed_total_price"
      :can-pay="!isBookingUiV2LivePreview && isBookingUiV2SelectionComplete"
      :payment-preview-only="isBookingUiV2LivePreview"
      :pay-loading="pay_loading"
      :local-safety-preview="isBookingUiV2LocalSafetyPreview"
      :guest-note-enabled="guestNoteEnabled"
      :guest-note="guestNote"
      :guest-note-max-length="guestNoteMaxLength"
      @update-guest-note="updateGuestNote"
      @select-concept="selectBookingUiV2Concept"
      @concept-detail="openConceptPatternDialog"
      @select-staff="selectStaff"
      @staff-detail="handleStaffCardClick"
      @date-updated="dateUpdated_handler"
      @select-time="selected_time = $event"
      @select-product="selectProduct"
      @toggle-option="onOptionProductChange"
      @pay="showRefundPolicyDialog"
    />
    <template v-else>
    <!-- 페이지 제목 -->
    <div v-if="isDispTitle">
      <div v-if="title.secure" style="position: absolute; left: calc(50% - 142px); top: 50px" class="mx-auto">
        <img src="@/assets/reservation/promotion-deco.png" width="300" />
      </div>
      <ReservationTitleLogo v-if="title.secure" :warning="true"> {{ title.name }} <br v-if="title.name" />{{ $t("page.reservation.secret-title") }} </ReservationTitleLogo>
      <TitleLogo v-else :title="$t('page.reservation.package-title')" :warning="false" />
    </div>

    <v-sheet v-if="!loading" style="padding-bottom: 100px; position: relative">
      <TipTapReader v-if="desc_options.content && !skipPromotionIntro" class="tiptap_style pa-0" :options="desc_options" :key="descComponentKey + 'des'" style="min-height: 0px" />

      <!-- 컨셉 선택 (새 컴포넌트) -->
      <BookingConceptSelector
        class="mt-8"
        :step="1"
        :title="$t('page.reservation.concept-select-title')"
        :selected-concept="selected_concept"
        :disabled="!!(promotionPriceInfo && promotionPriceInfo.conceptId)"
        :empty-text="$t('button.selectConcept')"
        @select="openSelectConcept"
        @detail="openConceptPatternDialog"
      />

      <BookingStaffSelector
        v-if="showStaffSelectionArea"
        class="mt-8 mx-1 mx-md-2"
        :step="2"
        :title="$t('page.reservation.maker-select-title')"
        :staff-list="staffForSelectedTime"
        :selected-id="selectedStaffId"
        :loading="staffLoading"
        :empty-text="selected_concept ? $t('page.reservation.staff-select-empty') : $t('page.reservation.staff-select-need-concept')"
        @click="handleStaffCardClick"
      />

      <div style="margin-bottom: 100px" class="mt-8">
        <div class="booking-step-panel mx-1 mx-md-2">
          <div class="booking-step-panel__header">
            <BookingStepHeader :step="showStaffSelectionArea ? 3 : 2" :title="dateStepLabel" />
          </div>
          <div class="dateSelectBox">
            <!-- 날짜 선택 -->
            <DateSelector
              :color="$vuetify.theme.currentTheme.primary"
              :width="330"
              class="mt-7 mb-4 mx-auto"
              @dateUpdated="dateUpdated_handler"
              :dateInit="date"
              :key="'DateSelector' + dateSelectComponentKey"
            />
            <!-- 시간 선택 -->
            <div v-if="!timeLoading" class="mx-auto mt-5" style="width: 328px">
              <!-- 작가 휴무일 메시지 -->
              <div v-if="staffHolidayInfo.isHoliday" class="text-center mt-10 mb-8 text-body-1" style="color: #8d8d8d"">                
                  {{ $t("page.reservation.lafia-no-available-time") }}
              </div>
              
              <div v-else-if="isPastSelectedDate" class="text-center mt-10 mb-8 text-body-1" style="color: #8d8d8d">
                {{ $t("page.reservation.lafia-past-date-warning") }}
              </div>
              <div v-else-if="isSameDayReservationBlocked" class="text-center mt-10 mb-8 text-body-1" style="color: #8d8d8d">
                {{ $t("page.reservation.lafia-same-day-reservation-not-allowed") }}
              </div>
              <div v-else-if="available_time_list.length > 0" class="d-flex justify-start flex-wrap" :key="timeSelectcomponentKey + 'time'">
                <v-btn
                  v-for="item in available_time_list"
                  :key="item"
                  height="36"
                  depressed
                  class="timeSelectBtn rounded-10 px-3 mb-1 my-1"
                  :class="{ enable: selected_time == item }"
                  :disabled="already_reserved_time_list.includes(item)"
                  @click="!already_reserved_time_list.includes(item) && selected_time != item ? (selected_time = item) : (selected_time = '')"
                >
                  {{ item }}
                </v-btn>
              </div>
              <div v-else class="text-center mt-10 mb-8 text-body-1" style="color: #8d8d8d">
                <template v-if="isPremiumProduct && !selectedStaffId">
                  {{ $t("page.reservation.please-select-staff") }}
                </template>
                <template v-else-if="isOutsidePromotionPeriod">
                  {{ $t("page.reservation.lafia-unavailable-date") }}
                </template>
                <template v-else>
                  {{ $t("page.reservation.lafia-no-available-time") }}
                </template>
              </div>
            </div>

            <!-- 로딩 (시간 선택) -->
            <v-progress-circular v-if="timeLoading" indeterminate color="primary" size="40" style="height: 72px" class="d-flex mx-auto mt-8"></v-progress-circular>
          </div>
        </div>
        <div style="margin-top: 10px; padding-bottom: 100px">
          <!-- 동적으로 products 배열을 렌더링 -->
          <template v-if="sortedProducts.length > 0">
            <component v-for="product in sortedProducts" :key="product.id" :is="getProductComponentName(product)" v-bind="getProductProps(product)" v-on="getProductEvents(product)" />
          </template>

          <!-- 옵션 상품 표시 -->
          <template v-if="filteredOptionProducts.length > 0">
            <div class="px-8 mt-8 mb-4 text-center" style="color: #545454">
              <div class="font-danjunghae" style="font-size: 34px">{{ $t("page.reservation.option-products-title") }}</div>
              <div class="text-body-1">{{ $t("page.reservation.lafia-reservation-step3") }}</div>
            </div>

            <template v-for="optionProduct in filteredOptionProducts">
              <!-- 패턴 추가 옵션인 경우 PatternOptionProductItem 사용 -->
              <PatternOptionProductItem
                v-if="isPatternOption(optionProduct)"
                :key="'pattern-option-' + optionProduct.id"
                :product="optionProduct"
                :value="isOptionProductSelected(optionProduct.id)"
                :pattern-count="getPatternCount()"
                @change="onOptionProductChange"
              />
              <!-- 일반 옵션인 경우 OptionProductItem 사용 -->
              <OptionProductItem v-else :key="'option-' + optionProduct.id" :product="{ ...optionProduct, price: optionProductPrice(optionProduct) }" :value="isOptionProductSelected(optionProduct.id)" @change="onOptionProductChange({ ...$event, product: optionProduct })" />
            </template>
          </template>
        </div>
      </div>

      <!-- 예약하기 하단 -->
      <v-sheet style="width: 100%; max-width: 580px; position: fixed; bottom: 72px; padding: 4px 24px; border-top: 1px solid #e0e0e0; z-index: 15; background-color: white">
        <div class="d-flex align-center justify-space-between">
          <!-- 결제하기 버튼 -->
          <v-btn
            large
            color="primary"
            style="font-size: 20px; min-width: 140px"
            class="rounded-10 darken-1"
            depressed
            :disabled="selected_time == '' || !persons || !selected_concept || !selectedProduct || (isPremiumProduct && !selectedStaffId)"
            @click="showRefundPolicyDialog()"
            :loading="pay_loading"
          >
            {{ $t("button.pay") }}
          </v-btn>

          <!-- 최종가격 -->
          <div class="text-right font-danjunghae" style="font-size: 28px; color: #5b4a62">
            <span class="text-body-1 grey--text text--darken-2 mr-2">{{ $t("page.reservation.total-amount") }}</span>
            {{ $toComma(computed_total_price) }}<span class="text-body-2 ml-1 grey--text text--darken-2">{{ $t("common.krw") }}</span>
          </div>
        </div>
      </v-sheet>
    </v-sheet>
    <v-sheet v-else class="text-center mt-14">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
    </v-sheet>
    </template>
    <!-- <ConceptSelectDialog
      :visible="show_concept_select"
      :product-type="productType"
      @close="show_concept_select = false"
      @input="(e) => (show_concept_select = e)"
      :key="'concept' + show_concept_select_componentKey"
      @apply="(item) => (selected_concept = item)"
    /> -->
    <ConceptPatternDialog
      :visible="show_concept_pattern_dialog"
      :concept="selected_concept"
      @close="show_concept_pattern_dialog = false"
      @input="(e) => (show_concept_pattern_dialog = e)"
      :key="'pattern' + show_concept_pattern_dialog_componentKey"
    />
    <StaffDetailDialog
      :visible="show_staff_detail_dialog"
      :staff="tempSelectedStaff"
      @cancel="handleStaffDialogCancel"
      @select="handleStaffDialogSelect"
      @input="(e) => (show_staff_detail_dialog = e)"
      :key="'staff' + show_staff_detail_dialog_componentKey"
    />
    <NormalWarningDialog :msg="warning_dialog_messsage" @input="warningDialogInputEvent" @confirm="confirmDialog" :visible="show_warning_dialog" :key="'normal' + show_warning_dialog_componentKey" />

    <!-- <RefundPolicyGuideDialog
        @agree="agreeRefundPolicy"
        @cancel="show_refund_policy = false"
        @input="(e) => (show_refund_policy = e)"
        :visible="show_refund_policy"
        :key="refund_policy_dialog_componentKey"
      /> -->
    <CautionMesageDialog
      @agree="agreeRefundPolicy"
      @cancel="show_refund_policy = false"
      @input="(e) => (show_refund_policy = e)"
      :visible="show_refund_policy"
      :product-type="productType"
      :key="refund_policy_dialog_componentKey"
    />
  </div>
</template>
<script>
import { computed } from "vue"
import { mapActions, mapGetters, mapState } from "vuex"
import TitleLogo from "@/views/components/TitleLogo"
import DateSelector from "@/components/DateSelector"
import ReservationTitleLogo from "@/views/components/ReservationTitleLogo"
import LafiaOptionItem from "@/views/reservation/buy/components/LafiaOptionItem"
import LafiaOptionItemExt from "@/views/reservation/buy/components/LafiaOptionItemExt"
import LafiaOptionGroupItem from "@/views/reservation/buy/components/LafiaOptionGroupItem"
import ConceptSelectDialog from "@/views/components/ConceptSelectDialog"
import TipTapReader from "@/components/tiptap/Reader"
import { OperationTimeList, Holidays } from "@/constant"
import NormalWarningDialog from "@/views/components/NormalWarningDialog"
// import RefundPolicyGuideDialog from "@/views/components/RefundPolicyGuideDialog"
import payletterService from "@/api/payletter-service"
import { getValidPayerEmail } from "@/util/utils"
import CautionMesageDialog from "@/views/components/CautionMesageDialog"
import OptionProductItem from "@/views/reservation/buy/components/OptionProductItem"
import PatternOptionProductItem from "@/views/reservation/buy/components/PatternOptionProductItem"
import optionProductService from "@/api/optionProductService"
import { optionUnitPrice } from "@/util/optionPricing"
import { isPromotionLinkOpen } from "@/util/promotionWindow"
import { quoteGroupProduct } from "../../../../express/services/group-pricing.cjs"
const { readValidBookingDraft } = require("@/util/bookingDraft")
const { normalizeTravelerCode } = require("@/util/travelerAttribution")
import BookingStaffSelector from "@/views/booking/components/StaffSelector"
import BookingStepHeader from "@/views/booking/components/StepHeader"
import BookingConceptSelector from "@/views/booking/components/ConceptSelector"
import ConceptPatternDialog from "@/views/booking/components/ConceptPatternDialog"
import StaffDetailDialog from "@/views/booking/components/StaffDetailDialog"
const BookingUiV2 = () => import(/* webpackChunkName: "booking-ui-v2" */ "@/views/booking/GPT/ui-v2/BookingUiV2")

const DEFAULT_BRANCH_CODE = "HONGDAE"
const BOOKING_DRAFT_KEY = "lafia_booking_draft_v2"
const BOOKING_DRAFT_VERSION = 2
const BOOKING_DRAFT_TTL = 30 * 60 * 1000
// 결제하기 → 로그인으로 갈 때 담은 것은 하루 둔다(확인 창이 '선택하신 정보는 없어지지 않는다'고 약속하므로, 가입이 오래 걸려도 지우지 않는다).
// 시간·작가는 돌아올 때 다시 확인하므로 길게 둬도 마감된 시간으로 결제되지 않는다
const BOOKING_DRAFT_LOGIN_TTL = 24 * 60 * 60 * 1000
// 결제하기 → 로그인(가입 포함) → 돌아오기에 넉넉한 시간. 이보다 늦게 돌아오면 고른 것만 되살리고 결제 동의 창은 열지 않는다
const RESUME_PAYMENT_TTL = 15 * 60 * 1000
export default {
  props: {
    branchCode: {
      type: String,
      default: DEFAULT_BRANCH_CODE,
    },
  },
  components: {
    TitleLogo,
    DateSelector,
    ReservationTitleLogo,
    LafiaOptionItem,
    LafiaOptionItemExt,
    LafiaOptionGroupItem,
    ConceptSelectDialog,
    TipTapReader,
    NormalWarningDialog,
    // RefundPolicyGuideDialog,
    CautionMesageDialog,
    OptionProductItem,
    PatternOptionProductItem,
    BookingStaffSelector,
    BookingStepHeader,
    BookingConceptSelector,
    ConceptPatternDialog,
    StaffDetailDialog,
    BookingUiV2,
  },

  provide() {
    return {
      promotion_info: computed(() => this.promotionPriceInfo),
      concepts: computed(() => this.selectableConceptList),
    }
  },
  data: () => ({
    loading: true,
    promotionPriceInfo: null,

    date: null,
    dateSelectComponentKey: 0,

    timeLoading: false,
    time_option_list: OperationTimeList,
    available_time_list: [],

    selected_time: "",
    timeSelectcomponentKey: 0,

    already_reserved_time_list: [],

    selectedProduct: null,

    descComponentKey: 0, //desc html
    //TipTap
    desc_options: {
      content: "",
      editable: false,
      supportImage: true,
      supportVideo: true,
    },

    tossPayments: null,
    reservation_dogin: false,

    //컨셉 선택
    //show_concept_select_componentKey: 0,
    //show_concept_select: false,

    selected_concept: null,
    productType: "makershot", // productType 예약 상품 타입 ( makershot,premium)

    //컨셉 패턴 상세
    show_concept_pattern_dialog: false,
    show_concept_pattern_dialog_componentKey: 0,

    //작가 상세
    show_staff_detail_dialog: false,
    show_staff_detail_dialog_componentKey: 0,

    persons: 1,
    groupProductPersons: {},

    //결제 진행중
    pay_loading: false,

    //warning dialog
    show_warning_dialog: false,
    warning_dialog_messsage: "",
    show_warning_dialog_componentKey: 0,

    show_refund_policy: false,
    refund_policy_dialog_componentKey: 0,

    conceptList: [],

    // 개별 운영시간 정보 추가
    individual_operation_time: null,
    // 현재 사용할 운영시간 정보 (개별 운영시간이 있으면 우선, 없으면 기본 운영시간 사용)
    current_operation_time: null,

    // 옵션 상품 관련
    optionProducts: [],
    selectedOptionProducts: [],

    // 작가 정보
    staffLoading: false,
    staffList: [],
    selectedStaffId: null,
    tempSelectedStaff: null, // 다이얼로그에서 보여줄 임시 작가 정보
    // 프리미엄: 고른 날짜에 작가별로 비어 있는 시간({작가 id: [시간]}). 시간을 먼저 고르고 그 시간에 되는 작가를 고른다.
    staffOpenTimes: {},
    premiumScheduleRequest: 0,

    // 작가 휴일 정보
    staffHolidayInfo: {
      isHoliday: false,
      comment: null,
    },
    restoringBookingDraft: false,
    // 결제하기 → 로그인 → 돌아온 손님에게 다 불러온 뒤 할 일: "pay"(결제 동의 창) · "time-taken"(그새 마감 안내)
    afterLoginStep: "",
    // 시간을 이미 불러온 날(YYYY-MM-DD). 날짜 칸이 같은 날을 다시 알려도 고른 시간을 지우지 않는다
    timesLoadedDay: "",
    guestNote: "",
    guestNoteMaxLength: 150,
    bookingDraftSaveTimer: null,
  }),

  watch: {
    lang() {
      console.log("watch lang")
      this.setDescription()
    },

    selected_concept(newVal, oldVal) {
      if (this.restoringBookingDraft) {
        return
      }
      if (newVal) {
        if (this.isPremiumProduct) {
          this.handleConceptChangedForStaff()
        }
      } else if (oldVal) {
        this.selectedStaffId = null
        this.staffList = []
        this.staffLoading = false
      } else {
        this.selectedStaffId = null
      }
    },
    staffCandidates() {
      this.syncStaffSelection()
      if (this.isPremiumProduct && this.date && !this.restoringBookingDraft) {
        this.refreshAvailableTimes()
      }
    },
    selected_time() {
      if (this.isPremiumProduct && !this.restoringBookingDraft) {
        this.syncStaffSelection()
      }
    },
    selectedStaffId(newVal, oldVal) {
      if (this.restoringBookingDraft) {
        return
      }
      if (!this.isPremiumProduct || newVal === oldVal) {
        return
      }
      if (newVal && this.selected_time && !this.isStaffOpenAt(newVal, this.selected_time)) {
        this.selected_time = ""
      }
    },
    bookingDraftSnapshot: {
      deep: true,
      handler() {
        this.queueBookingDraftSave()
      },
    },
  },

  async mounted() {
    if (this.isBookingUiV2LocalSafetyPreview) {
      this.loading = false
      return
    }

    this.tossInit()

    try {
      if (!this.operation_time) {
        const time_info = await this.loadOperationTime()
        if (!time_info) {
          alert(this.$t("alert.error_page"))
          this.$router.go(-1)
        }
      }
      this.current_operation_time = this.operation_time

      console.log("컨셉 로드 전")
      this.conceptList = await this.loadConceptsAll()

      if (this.$route.query.conceptId) {
        this.selected_concept = this.conceptList.find((e) => e.id == this.$route.query.conceptId)
        this.productType = this.selected_concept.productType
      }

      const res = await this.getPromotionPrice({ promotionCode: this.getPromotionCode, photoUse: this.$route.query.photoUse })
      console.log("getPromotionPrice res", res, this.getPromotionCode)
      this.promotionPriceInfo = res.promotionPrice

      if (this.getPromotionCode && !this.promotionPriceInfo) {
        alert(this.$t("alert.error_invalid_product_page"))
        this.$router.go(-1)
        return
      }

      let _promotionInfo = this.promotionPriceInfo
      if (_promotionInfo) {
        // 할인 링크 열림·닫힘은 서버와 같게 시각으로 본다(날짜로 보면 19:00 공개가 그날 0시에 열림)
        if (!isPromotionLinkOpen(_promotionInfo)) {
          alert(this.$t("alert.error_invalid_product_page"))
          this.$router.replace("/")
          return
        }
        if (_promotionInfo.isIndividual) {
          this.productType = "premium"
        }

        //선택한 프로모션이 특정 컨셉용인지 확인
        if (_promotionInfo.conceptId) {
          this.selected_concept = this.conceptList.find((e) => e.id === _promotionInfo.conceptId)
          this.productType = this.selected_concept.productType
        }
      }

      if (this.selected_concept && this.selected_concept.status !== "노출" && !this.promotionPriceInfo) {
        alert("이 컨셉은 펀딩 페이지에서 예약해 주세요.")
        this.$router.replace("/funding")
        return
      }

      console.log("옵션 상품 로드 전")
      // 옵션 상품 로드
      await this.loadOptionProducts()
      console.log("옵션 상품 로드 후")

      // 프리미엄 상품인 경우 초기 날짜를 다음날로 설정
      if (this.isPremiumProduct) {
        const nextDay = this.dayjs().add(1, "day").startOf("day")
        const promotionStart = this.promotionPriceInfo
          ? this.dayjs(this.promotionPriceInfo.startAt).startOf("day")
          : null
        this.date = promotionStart && promotionStart.isValid() && nextDay.isBefore(promotionStart, "day")
          ? promotionStart
          : nextDay
        this.dateSelectComponentKey++
        // 첫 날짜의 빈 시간을 바로 불러온다. 작가 목록이 날짜보다 먼저 도착하면 staffCandidates 감시가 날짜 없이 지나가기 때문
        await this.refreshAvailableTimes()
      }
      await this.restoreBookingDraft()
    } catch (e) {
      console.log(e)
      alert(this.$t("alert.error_page"))
      this.$router.go(-1)
    } finally {
      this.setDescription()
      this.loading = false
      const step = this.afterLoginStep
      this.afterLoginStep = ""
      if (step === "pay") this.$nextTick(() => this.showRefundPolicyDialog())
      if (step === "time-taken") this.$nextTick(() => alert(this.$t("page.mypage.reserv.time-change.error-taken")))
    }
  },
  beforeDestroy() {
    window.clearTimeout(this.bookingDraftSaveTimer)
  },
  computed: {
    ...mapState(["default_price", "lafia_user", "lang", "operation_time", "isSupportAbroadPay"]),
    ...mapGetters(["isKRW"]),

    isBookingUiV2() {
      if (process.env.VUE_APP_SAFETY_MODE === "true") {
        return true
      }

      return this.productType === "premium"
    },

    isBookingUiV2LocalSafetyPreview() {
      return process.env.VUE_APP_SAFETY_MODE === "true" && this.isBookingUiV2
    },

    isBookingUiV2LivePreview() {
      return process.env.VUE_APP_BOOKING_UI_V2_LIVE_PREVIEW === "true"
    },

    isBookingUiV2SelectionComplete() {
      return this.selected_time !== "" && !!this.persons && !!this.selected_concept && !!this.selectedProduct && (!this.isPremiumProduct || !!this.selectedStaffId)
    },

    bookingDraftSnapshot() {
      return {
        promotionCode: this.getPromotionCode || null,
        conceptId: this.selected_concept ? this.selected_concept.id : null,
        staffId: this.selectedStaffId || null,
        date: this.date && typeof this.date.format === "function" ? this.date.format("YYYY-MM-DD") : null,
        time: this.selected_time || "",
        productId: this.selectedProduct ? this.selectedProduct.id : null,
        persons: Number(this.persons) || 1,
        options: this.selectedOptionProducts.map((option) => ({ id: option.id, quantity: Number(option.quantity) || 1 })),
        lang: this.lang,
        guestNote: this.guestNote,
      }
    },

    // 체험단 손님 한마디 칸: 사진 사용에 동의하고 들어온 행사 예약에서만 보인다(10/8 신작홍보 요청)
    guestNoteEnabled() {
      return (
        this.isBookingUiV2 &&
        this.$route.query.photoUse === "agree" &&
        !!this.promotionPriceInfo &&
        (!this.selectedProduct || this.selectedProduct.photoUsageConsent === true)
      )
    },

    getMaxAvailableRoom() {
      if (this.getPromotionCode === "PrincessBall") {
        return 15
      }
      return 1 // 2 -> 3 -> 1
    },
    skipPromotionIntro() {
      // 상세에서 이어온 예약만 소개를 생략한다. 가격·동의·주문에는 사용하지 않는다.
      return this.$route.query.bookingIntro === "skip"
        && typeof this.$route.query.event === "string"
        && !!this.$route.query.event.trim()
        && !!(this.promotionPriceInfo && this.promotionPriceInfo.conceptId)
    },
    getPromotionCode() {
      const eventQuery = this.$route.query.event
      if (eventQuery) {
        return eventQuery
      }
      // productType이 premium이고 query에 event가 없으면 'premium' 반환
      if (this.productType === "premium") {
        return "default_premium"
      }
      return eventQuery
    },
    getTraveler() {
      return normalizeTravelerCode(this.$route.query.traveler)
    },

    title() {
      if (this.promotionPriceInfo) {
        return { name: this.getTitle(this.promotionPriceInfo), secure: true }
      } else {
        return { name: "", secure: false }
      }
    },

    // 동적 상품 리스트를 정렬하여 반환
    sortedProducts() {
      var products = []
      try {
        if (this.promotionPriceInfo && this.promotionPriceInfo.products) {
          products = JSON.parse(this.promotionPriceInfo.products)
        } else {
          products = JSON.parse(this.default_price.products)
        }
        const photoUse = String(this.$route.query.photoUse || "").trim()
        if (photoUse === "agree" || photoUse === "private") {
          const expectedConsent = photoUse === "agree"
          products = products
            .filter(product => (
              typeof product.photoUsageConsent !== "boolean" ||
              product.photoUsageConsent === expectedConsent
            ))
            .map(product => {
              const privateSurcharge = Math.max(0, Number(product.photoUsagePrivateSurcharge) || 0)
              if (typeof product.photoUsageConsent === "boolean" || privateSurcharge <= 0) return product
              return {
                ...product,
                price: product.groupPricing ? product.price : Math.max(0, Number(product.price) || 0) + (expectedConsent ? 0 : privateSurcharge),
                photoUsageConsent: expectedConsent,
              }
            })
        }
        return products.sort((a, b) => a.order - b.order)
      } catch (e) {
        console.error("Failed to parse products:", e)
        return []
      }
    },

    selectedUnitPrice() {
      const quote = quoteGroupProduct(this.selectedProduct, Number(this.persons), true)
      return quote ? quote.unitPrice : Number(this.selectedProduct && this.selectedProduct.price) || 0
    },

    // 최종 가격 계산
    computed_total_price() {
      let priceInfo = this.promotionPriceInfo ? this.promotionPriceInfo : this.default_price
      let totalPrice = 0

      // 기본 상품 가격 계산
      if (this.selectedProduct) {
        if (this.selectedProduct.type === "group") {
          totalPrice = Math.max(this.selectedUnitPrice * this.persons, 0)
        } else {
          totalPrice = Math.max(this.selectedUnitPrice, 0)
        }
      }

      // 옵션 상품 가격 추가 (수량 반영)
      this.selectedOptionProducts.forEach((optionProduct) => {
        const quantity = optionProduct.quantity || 1
        totalPrice += Math.max(this.optionProductPrice(optionProduct) * quantity, 0)
      })

      return totalPrice
    },

    selectableConceptList() {
      return this.conceptList.filter((e) => e.status === "노출")
    },

    isPremiumProduct() {
      return this.productType === "premium"
    },

    /*
    isIndividual() {
      return this.promotionPriceInfo != null && this.promotionPriceInfo.isIndividual
    },
    */

    isDispTitle() {
      //return this.promotionPriceInfo != null && !this.promotionPriceInfo.isIndividual
      return false
    },

    isReverseOrder() {
      return this.promotionPriceInfo != null && this.promotionPriceInfo.isReverseOrder
    },

    normalizedBranchCode() {
      return (this.branchCode || DEFAULT_BRANCH_CODE).toString().trim().toUpperCase()
    },

    bookingBranchCode() {
      return this.normalizedBranchCode || DEFAULT_BRANCH_CODE
    },

    shouldFetchStaffForConcept() {
      return !!this.bookingBranchCode && this.isPremiumProduct
    },

    staffCandidates() {
      if (!Array.isArray(this.staffList) || !this.selected_concept) {
        return []
      }
      const conceptId = String(this.selected_concept.id)
      return this.staffList.filter((staff) => Array.isArray(staff.conceptIds) && staff.conceptIds.map(String).includes(conceptId))
    },

    showStaffSelectionArea() {
      return this.shouldFetchStaffForConcept
    },

    staffForSelectedTime() {
      if (!this.isPremiumProduct || !this.selected_time) {
        return this.staffCandidates
      }
      return this.staffCandidates.filter((staff) => this.isStaffOpenAt(staff.id, this.selected_time))
    },

    // 메이커 창: 모든 메이커를 보여 주되, 고른 시간에 촬영이 없는 메이커는 unavailable로 표시
    staffChoicesForSelectedTime() {
      const openIds = this.staffForSelectedTime.map((staff) => staff.id)
      return this.staffCandidates.map((staff) => ({ ...staff, unavailable: !openIds.includes(staff.id) }))
    },

    selectedStaff() {
      return this.staffCandidates.find((staff) => staff.id === this.selectedStaffId) || null
    },

    dateStepLabel() {
      return this.$t("page.reservation.date-select-title")
    },

    isPastSelectedDate() {
      if (!this.date || !this.dayjs) {
        return false
      }
      return this.date.isBefore(this.dayjs(), "day")
    },

    isSameDayReservationBlocked() {
      if (!this.isPremiumProduct || !this.date || !this.dayjs) {
        return false
      }
      return this.date.isSame(this.dayjs(), "day")
    },

    // 프로모션 상품인데 선택한 날짜가 프로모션 예약 가능 기간을 벗어난 경우
    isOutsidePromotionPeriod() {
      if (!this.promotionPriceInfo || !this.date || !this.dayjs) {
        return false
      }
      return !this.isDateWithinPromotionPeriod(this.date)
    },

    // 필터링된 옵션 상품 목록
    filteredOptionProducts() {
      if (!Array.isArray(this.optionProducts)) {
        return []
      }

      return this.optionProducts.filter((product) => {
        // title 추출
        let title = ""
        if (typeof product.title === "string") {
          try {
            const parsed = JSON.parse(product.title)
            title = parsed.kr || product.title
          } catch {
            title = product.title
          }
        } else if (typeof product.title === "object") {
          title = product.title.kr || ""
        }

        // "패턴 추가"가 포함된 경우 조건 체크
        if (title.includes("패턴 추가")) {
          // 상품을 선택하지 않았거나 group 타입이면 표시 안함
          if (!this.selectedProduct || this.selectedProduct.type === "group") {
            return false
          }
          // selected_concept가 있고 pattern_others_image가 배열이며 길이가 0보다 큰 경우만 표시
          return this.selected_concept && Array.isArray(this.selected_concept.pattern_others_image) && this.selected_concept.pattern_others_image.length > 0
        }

        // 그 외의 경우는 모두 표시
        return true
      })
    },
  },

  methods: {
    ...mapActions(["getPromotionPrice", "tossInit", "loadOperationTime", "loadConceptsAll", "cancelPayStatusUpdate", "loadIndividualOperationTime"]),

    isDateWithinPromotionPeriod(date) {
      if (!this.promotionPriceInfo || !date) {
        return true
      }
      const startAt = this.dayjs(this.promotionPriceInfo.startAt)
      const endAt = this.dayjs(this.promotionPriceInfo.endAt)
      if (!startAt.isValid() || !endAt.isValid()) {
        return true
      }
      return date.isBetween(startAt, endAt, "day", "[]")
    },

    updateGuestNote(value) {
      this.guestNote = String(value || "").slice(0, this.guestNoteMaxLength)
      this.queueBookingDraftSave()
    },

    queueBookingDraftSave() {
      if (this.loading || this.restoringBookingDraft) {
        return
      }
      window.clearTimeout(this.bookingDraftSaveTimer)
      this.bookingDraftSaveTimer = window.setTimeout(() => this.saveBookingDraft(), 150)
    },

    // 고른 것을 30분 동안 담아 둔다(프리미엄·메이커 샷·행사 모두). resumePayment는 결제하기를 누르다 로그인으로 간 표시
    saveBookingDraft({ resumePayment = false } = {}) {
      window.clearTimeout(this.bookingDraftSaveTimer)
      const snapshot = this.bookingDraftSnapshot
      const hasSelection = snapshot.conceptId || snapshot.staffId || snapshot.time || snapshot.productId || snapshot.options.length
      try {
        if (!hasSelection) {
          window.localStorage.removeItem(BOOKING_DRAFT_KEY)
          return
        }
        const now = Date.now()
        window.localStorage.setItem(
          BOOKING_DRAFT_KEY,
          JSON.stringify({
            version: BOOKING_DRAFT_VERSION,
            createdAt: now,
            expiresAt: now + (resumePayment ? BOOKING_DRAFT_LOGIN_TTL : BOOKING_DRAFT_TTL),
            ...snapshot,
            resumePayment,
          })
        )
      } catch (error) {
        console.warn("예약 선택 임시 저장 실패", error)
      }
    },

    // 로그인 안내: 고른 것이 그대로 남는다는 것을 먼저 말한다
    loginNeededMessage() {
      return this.$t("alert.login_keeps_booking", { minutes: Math.round(BOOKING_DRAFT_TTL / 60000) })
    },

    // 결제하기를 눌렀는데 로그인이 안 돼 있으면: 고른 것을 담고 로그인 화면으로(돌아오면 결제 동의 창부터)
    goLoginKeepingBooking() {
      this.saveBookingDraft({ resumePayment: true })
      if (this.promotionPriceInfo && this.getPromotionCode) {
        this.$store.commit("ADD_PROMOTION_CODE", this.getPromotionCode)
      }
      alert(this.loginNeededMessage())
      this.$router
        .push({
          path: "/auth/login",
          query: { callbackUrl: this.$route.fullPath },
        })
        .catch(() => {})
    },

    // 되살린 선택이 저장한 것과 같은지(날짜·시간·작가·상품·인원·옵션). 다르면 결제 동의 창을 바로 열지 않는다
    sameBookingChoice(a, b) {
      const key = (s) =>
        JSON.stringify([
          s.date || "",
          s.time || "",
          String(s.staffId || ""),
          String(s.productId || ""),
          Number(s.persons) || 1,
          (s.options || []).map((option) => `${option.id}:${Number(option.quantity) || 1}`).sort(),
        ])
      return key(a) === key(b)
    },

    readBookingDraft() {
      return readValidBookingDraft({
        storage: window.localStorage,
        key: BOOKING_DRAFT_KEY,
        expectedVersion: BOOKING_DRAFT_VERSION,
        now: Date.now(),
        promotionCode: this.getPromotionCode,
        routeConceptId: this.$route.query.conceptId,
      })
    },

    clearBookingDraft() {
      try {
        window.localStorage.removeItem(BOOKING_DRAFT_KEY)
      } catch (error) {}
    },

    bookingProductTier(product) {
      const registeredTier = String(product && (product.tier || product.grade || product.productTier) ? product.tier || product.grade || product.productTier : "").toLowerCase()
      if (/gold|골드|ゴールド/.test(registeredTier)) {
        return "gold"
      }
      if (/basic|베이직|ベーシック/.test(registeredTier)) {
        return "basic"
      }
      const title = product && product.title ? JSON.stringify(product.title) : ""
      const subtitle = product && product.subtitle ? JSON.stringify(product.subtitle) : ""
      return /gold|골드|ゴールド|당일|当日|print|인화|プリント/i.test(`${title} ${subtitle}`) ? "gold" : "basic"
    },

    draftOptionRule(product) {
      const code = String(product && product.code ? product.code : "").trim().toLowerCase()
      const descriptionCode = String(product && product.descriptionCode ? product.descriptionCode : "").trim().toLowerCase()
      const family = code.startsWith("concept_") || descriptionCode === "op_addcon" || descriptionCode === "op_addcon2" ? "concept" : code.startsWith("retouch") || descriptionCode === "op_ret" ? "retouch" : ""
      const mode = /(?:^|_)group(?:_|\+)/.test(code) ? "group" : /(?:^|_)solo(?:_|\+)/.test(code) ? "solo" : ""
      const tier = /(?:^|_)gold(?:_|\+)/.test(code) ? "gold" : /(?:^|_)basic(?:_|\+)/.test(code) ? "basic" : ""
      return { family, mode, tier }
    },

    isDraftOptionAllowed(product, selectedProduct) {
      if (!selectedProduct) {
        return false
      }
      const rule = this.draftOptionRule(product)
      if (!rule.family) {
        return true
      }
      const mode = selectedProduct.type === "group" ? "group" : "solo"
      const tier = this.bookingProductTier(selectedProduct)
      return (!rule.mode || rule.mode === mode) && (!rule.tier || rule.tier === tier)
    },

    async restoreBookingDraft() {
      const draft = this.readBookingDraft()
      if (!draft) {
        return
      }
      this.restoringBookingDraft = true
      try {
        if (!(this.promotionPriceInfo && this.promotionPriceInfo.conceptId) && draft.conceptId) {
          const concept = this.conceptList.find((item) => String(item.id) === String(draft.conceptId))
          if (concept) {
            this.selected_concept = concept
            this.productType = concept.productType
          }
        }

        if (this.selected_concept && this.isPremiumProduct) {
          await this.handleConceptChangedForStaff()
        }

        // 프리미엄은 내일부터, 메이커 샷·행사는 오늘부터 예약된다
        const restoredDate = draft.date ? this.dayjs(draft.date).startOf("day") : null
        const earliestDay = this.dayjs().add(this.isPremiumProduct ? 1 : 0, "day").startOf("day")
        if (
          restoredDate &&
          restoredDate.isValid() &&
          !restoredDate.isBefore(earliestDay, "day") &&
          this.isDateWithinPromotionPeriod(restoredDate)
        ) {
          this.date = restoredDate
          this.dateSelectComponentKey++
          await this.refreshAvailableTimes()
          if (draft.time && this.available_time_list.includes(draft.time) && !this.already_reserved_time_list.includes(draft.time)) {
            this.selected_time = draft.time
          } else {
            this.selected_time = ""
          }
        }

        // 작가는 되살린 시간에 비어 있을 때만 되살린다(시간 먼저 고르는 순서).
        // 고른 작가가 그 시간에 빠졌으면 다른 작가를 말없이 넣지 않고 시간부터 다시 고르게 한다
        if (this.isPremiumProduct) {
          const restoredStaff = draft.staffId && this.staffForSelectedTime.find((staff) => String(staff.id) === String(draft.staffId))
          if (restoredStaff) {
            this.selectedStaffId = restoredStaff.id
          } else {
            if (draft.staffId && this.selected_time) this.selected_time = ""
            this.syncStaffSelection()
          }
        }

        if (typeof draft.guestNote === "string") this.guestNote = draft.guestNote.slice(0, this.guestNoteMaxLength)

        const product = this.sortedProducts.find((item) => String(item.id) === String(draft.productId))
        if (product) {
          this.selectedProduct = product
          this.persons = product.type === "group" ? Math.min(Number(product.maxpersons) || 6, Math.max(2, Number(draft.persons) || 2)) : 1
          if (product.groupPricing) {
            this.persons = Math.trunc(this.persons)
            this.groupProductPersons = { ...this.groupProductPersons, [product.id]: this.persons }
          }
        }

        if (product && Array.isArray(draft.options)) {
          this.selectedOptionProducts = draft.options
            .map((saved) => {
              const option = this.optionProducts.find((item) => String(item.id) === String(saved.id))
              if (!option || !this.isDraftOptionAllowed(option, product)) {
                return null
              }
              return {
                ...option,
                quantity: Math.min(Number(option.maxCount) || 19, Math.max(1, Number(saved.quantity) || 1)),
              }
            })
            .filter(Boolean)
        }
      } finally {
        this.restoringBookingDraft = false
      }

      // '결제하기 → 로그인' 표시는 한 번만, 로그인하러 간 지 얼마 안 됐을 때만 쓴다.
      // 고른 것이 하나도 바뀌지 않고 되살아났으면 결제 동의 창부터 연다. 그새 고른 시간이 마감됐으면 말없이 비워 두지 않고 알린다
      if (draft.resumePayment === true) {
        const fresh = Date.now() - Number(draft.createdAt) <= RESUME_PAYMENT_TTL
        const unchanged = this.sameBookingChoice(this.bookingDraftSnapshot, draft)
        this.saveBookingDraft()
        if (fresh && this.lafia_user.is_logined) {
          if (unchanged && this.isBookingUiV2SelectionComplete) this.afterLoginStep = "pay"
          // '다른 손님이 먼저 예약' 안내는 날짜가 그대로 되살아났는데 시간만 빠졌을 때만(자정이 지나거나 행사 기간이 끝났거나 오늘 그 시간이 지나 버린 경우엔 까닭이 달라 띄우지 않는다)
          else if (draft.time && !this.selected_time && this.date && this.date.format("YYYY-MM-DD") === draft.date && !this.isPastTime(draft.time)) this.afterLoginStep = "time-taken"
        }
      }
    },

    selectBookingUiV2Concept(concept) {
      if (!concept || (this.promotionPriceInfo && this.promotionPriceInfo.conceptId)) {
        return
      }

      const conceptChanged = !this.selected_concept || String(this.selected_concept.id) !== String(concept.id)
      this.selected_concept = concept

      if (!conceptChanged) {
        return
      }

      this.selected_time = ""
      this.available_time_list = []
      this.already_reserved_time_list = []
      this.selectedProduct = null
      this.persons = 1
      this.selectedOptionProducts = []
      this.selectedStaffId = null
      this.staffHolidayInfo = {
        isHoliday: false,
        comment: null,
      }

      if (!this.isPremiumProduct && this.date) {
        this.refreshAvailableTimes()
      }
    },

    validateDateWindow() {
      let defaultExpireTime = null
      if (this.default_price && this.default_price.endAt) {
        defaultExpireTime = this.dayjs(this.default_price.endAt)
      }
      const autoEndAt = this.dayjs().add(3, "month").endOf("month")
      const diffMonth = this.date.startOf("month").diff(this.dayjs().startOf("month"), "month")

      const blockAll = () => {
        this.already_reserved_time_list = [...this.time_option_list]
      }

      if (this.date.isBefore(this.dayjs(), "date")) {
        blockAll()
        return false
      }
      if (defaultExpireTime && this.date.isAfter(defaultExpireTime, "date")) {
        blockAll()
        return false
      }
      if (this.date.isAfter(autoEndAt, "date")) {
        blockAll()
        return false
      }
      if ((this.date.day() === 0 || this.date.day() === 6) && diffMonth > 2) {
        blockAll()
        return false
      }

      return true
    },

    applyBaseTimeFilters() {
      this.already_reserved_time_list = []
      this.WorkTimeCheck(this.date)
      this.TodayTimePassCheck()
    },

    async handleConceptChangedForStaff() {
      console.log("handleConceptChangedForStaff")
      if (!this.shouldFetchStaffForConcept) {
        this.selectedStaffId = null
        this.staffList = []
        return
      }
      await this.fetchBranchStaff()
    },

    async fetchBranchStaff() {
      console.log("fetchBranchStaff")
      if (!this.shouldFetchStaffForConcept) {
        this.staffList = []
        return
      }

      const branchCode = this.bookingBranchCode
      if (!branchCode) {
        this.staffList = []
        return
      }
      this.staffLoading = true

      try {
        const { data } = await this.$http.get(`/api/booking/branches/${branchCode}/staff`)
        console.log("fetchBranchStaff data", data)
        this.staffList = Array.isArray(data)
          ? data.map((staff) => ({
              ...staff,
              conceptIds: Array.isArray(staff.conceptIds) ? staff.conceptIds : [],
            }))
          : []
      } catch (error) {
        console.error("staff load error", error)
        this.staffList = []
      } finally {
        this.staffLoading = false
        this.syncStaffSelection()
      }
    },

    handleStaffCardClick(staff) {
      if (!staff) {
        return
      }
      // 임시 작가 정보 저장 후 다이얼로그 열기
      this.tempSelectedStaff = staff
      this.show_staff_detail_dialog = true
      this.show_staff_detail_dialog_componentKey++
    },

    handleStaffDialogCancel() {
      // 취소 시 다이얼로그만 닫기
      this.show_staff_detail_dialog = false
      this.tempSelectedStaff = null
    },

    handleStaffDialogSelect() {
      // 선택 시 작가 ID 설정 후 다이얼로그 닫기
      if (this.tempSelectedStaff && this.tempSelectedStaff.id) {
        this.selectedStaffId = this.tempSelectedStaff.id
      }
      this.show_staff_detail_dialog = false
      this.tempSelectedStaff = null
    },

    selectStaff(userId) {
      if (!userId) {
        this.selectedStaffId = null
        return
      }
      if (this.selectedStaffId === userId) {
        return
      }
      this.selectedStaffId = userId
    },

    syncStaffSelection() {
      const availableIds = this.staffForSelectedTime.map((staff) => staff.id)
      if (availableIds.length === 0) {
        this.selectedStaffId = null
        return
      }

      if (this.selectedStaffId && availableIds.includes(this.selectedStaffId)) {
        return
      }

      this.selectedStaffId = availableIds.length === 1 ? availableIds[0] : null
    },

    getTitle(promotionPriceInfo) {
      let name = promotionPriceInfo && promotionPriceInfo.name
      return name ? `${name}` : ""
    },

    // 상품의 타이틀을 언어별로 가져오기
    getProductTitle(product) {
      if (!product || !product.title) return ""
      switch (this.lang) {
        case "en":
          return product.title.en || product.title.kr || ""
        case "jp":
          return product.title.jp || product.title.kr || ""
        default:
          return product.title.kr || ""
      }
    },

    // 상품의 서브타이틀을 언어별로 가져오기
    getProductSubtitle(product) {
      if (!product || !product.subtitle) return ""
      switch (this.lang) {
        case "jp":
          return product.subtitle.jp || product.subtitle.kr || ""
        case "en":
          return product.subtitle.en || product.subtitle.kr || ""
        default:
          return product.subtitle.kr || ""
      }
    },

    // 상품 타입에 따라 컴포넌트 이름 반환
    getProductComponentName(product) {
      if (product.type === "single") {
        return "LafiaOptionItem"
      } else {
        return "LafiaOptionGroupItem"
      }
    },

    // 상품에 대한 props 반환
    getProductProps(product) {
      const people = this.selectedProduct && this.selectedProduct.id === product.id
        ? Number(this.persons) : this.groupProductPersons[product.id] || 2
      const quote = quoteGroupProduct(product, people, true)
      const props = {
        src: {
          title: this.getProductTitle(product),
          subTitle: this.getProductSubtitle(product),
          price: quote ? quote.unitPrice : product.price,
        },
        selected: this.selectedProduct?.id == product.id,
        maxGroupMemberSize: product.maxpersons ? product.maxpersons : 6,
      }
      if (product.type !== "single") {
        // 되살린 인원을 인원 칸에 그대로 보인다
        props.initialCount = people
      }

      return props
    },

    // 상품에 대한 이벤트 핸들러 반환
    getProductEvents(product) {
      // option3는 특별한 이벤트 처리
      if (product.type === "group") {
        return {
          click: (e) => this.selectProduct(product, e),
          changeCount: this.changeCount,
        }
      }

      // 기본 클릭 이벤트
      return {
        click: () => this.selectProduct(product),
      }
    },

    async refreshAvailableTimes() {
      if (!this.date) {
        this.available_time_list = []
        this.already_reserved_time_list = []
        return
      }

      const day = this.date.format("YYYY-MM-DD")
      this.timesLoadedDay = ""
      if (this.isPremiumProduct) {
        await this.loadPremiumSchedule()
      } else {
        this.available_time_list = this.getWorkDayTime(this.date)
        await this.checkOpenTime()
      }
      if (this.date && this.date.format("YYYY-MM-DD") === day) {
        this.timesLoadedDay = day
      }
    },

    // 프리미엄 예약은 시간을 먼저 고른다: 이 컨셉을 찍는 작가 전원의 그날 빈 시간을 합쳐 보여 주고,
    // 한 명이라도 비어 있으면 그 시간을 연다. 작가는 고른 시간에 비어 있는 사람 중에서 고른다.
    async loadPremiumSchedule() {
      if (!this.isPremiumProduct) {
        return
      }

      const requestId = ++this.premiumScheduleRequest
      const candidates = this.staffCandidates
      this.selected_time = ""
      this.staffOpenTimes = {}
      this.staffHolidayInfo = {
        isHoliday: false,
        comment: null,
      }

      if (!candidates.length) {
        this.available_time_list = []
        this.already_reserved_time_list = []
        this.timeLoading = false
        return
      }

      this.already_reserved_time_list = []
      if (!this.validateDateWindow()) {
        this.available_time_list = []
        this.timeLoading = false
        return
      }

      this.applyBaseTimeFilters()

      const targetDate = this.date
      this.timeLoading = true
      try {
        const schedules = await Promise.all(
          candidates.map((staff) => this.fetchStaffDaySchedule(targetDate, staff.id).then((schedule) => ({ staffId: staff.id, ...schedule })))
        )
        if (requestId !== this.premiumScheduleRequest) {
          return
        }

        const isPrincessBall = this.getPromotionCode === "PrincessBall"
        const isBlocked = (slot) => (isPrincessBall ? (slot.count || 0) >= this.getMaxAvailableRoom : !slot.available)
        const openTimes = {}
        const allTimes = new Set()
        schedules.forEach((schedule) => {
          if (schedule.isHoliday) {
            return
          }
          schedule.slots.forEach((slot) => allTimes.add(slot.time))
          openTimes[schedule.staffId] = schedule.slots.filter((slot) => !isBlocked(slot)).map((slot) => slot.time)
        })
        const openUnion = new Set(Object.values(openTimes).flat())
        const times = Array.from(allTimes).sort((left, right) => this.timeToMinutes(left) - this.timeToMinutes(right))

        // 모두 쉬는 날: 휴무 안내 문구가 있으면 그것을, 없으면 '선택할 수 있는 시간이 없습니다'
        const holidayComment = schedules.every((schedule) => schedule.isHoliday) ? (schedules.find((schedule) => schedule.comment) || {}).comment : null
        if (holidayComment) {
          this.staffHolidayInfo = {
            isHoliday: true,
            comment: holidayComment,
          }
        }

        this.staffOpenTimes = openTimes
        this.available_time_list = times
        this.already_reserved_time_list = Array.from(new Set([...this.already_reserved_time_list, ...times.filter((time) => !openUnion.has(time))]))
      } catch (error) {
        console.error("staff schedule load error", error)
        if (requestId === this.premiumScheduleRequest) {
          this.staffOpenTimes = {}
          this.available_time_list = []
          this.already_reserved_time_list = []
        }
      } finally {
        if (requestId === this.premiumScheduleRequest) {
          this.timeLoading = false
        }
      }
    },

    isStaffOpenAt(staffId, time) {
      const times = this.staffOpenTimes[staffId]
      return Array.isArray(times) && times.includes(time)
    },

    timeToMinutes(time) {
      const [hour, minute] = String(time || "").split(":").map((part) => parseInt(part, 10))
      return (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0)
    },

    // 작가 한 명의 그날 일정(휴무 여부·시간 칸). 화면 상태는 바꾸지 않는다.
    async fetchStaffDaySchedule(targetDate, staffUserId) {
      const baseDate = targetDate ? this.dayjs(targetDate) : this.dayjs()
      const { data } = await this.$http.get(`/api/booking/staff/${staffUserId}/schedule`, {
        params: { date: baseDate.format("YYYY-MM-DD") },
      })
      if (data?.isHoliday) {
        return { isHoliday: true, comment: data.holidayComment || null, slots: [] }
      }
      return { isHoliday: false, comment: null, slots: Array.isArray(data?.slots) ? data.slots : [] }
    },

    // 결제 직전 재확인용: 고른 작가의 그날 시간 칸
    async getStaffScheduleSlots(targetDate, staffUserId = this.selectedStaffId) {
      if (!staffUserId) {
        return []
      }
      const schedule = await this.fetchStaffDaySchedule(targetDate, staffUserId)
      this.staffHolidayInfo = {
        isHoliday: schedule.isHoliday,
        comment: schedule.comment,
      }
      return schedule.slots
    },

    async dateUpdated_handler(date) {
      console.log("dateUpdated_handler")
      // 날짜 칸은 처음 그려질 때도 날짜를 알린다. 이미 그날 시간을 불러왔으면(되살린 예약 등) 고른 시간을 지우지 않는다
      if (this.date && this.date.isSame(date, "day") && this.timesLoadedDay === this.date.format("YYYY-MM-DD")) {
        return
      }
      this.date = date.startOf("day")
      /*
        if(this.isReverseOrder) {
          this.selectOption("option1") // 솔로샷이 맨 처음.
        } else {
          this.selectOption("option3", 2) // 기본 선택 이 option1 -> option3 인원2
        }
        */

      await this.refreshAvailableTimes()
      if (this.promotionPriceInfo) {
        this.checkPromotionDate(this.promotionPriceInfo)
      }
    },
    // 선택된 날짜의 닫힌 시간 확인하기
    async checkOpenTime() {
      this.timeLoading = true
      // 선택한 시간 초기화
      this.selected_time = ""
      this.already_reserved_time_list = []

      if (!this.validateDateWindow()) {
        this.timeLoading = false
        return
      }

      console.log("checkOpenTime check date: ", this.date.format("YYYY-MM-DD"))
      this.applyBaseTimeFilters()

      //연속 시간대 확인.
      try {
        await this.checkContinuouslyReservation()
      } catch (e) {
        console.log(e)
      }

      try {
        await this.todayTimeAvailableRoomCheck()
      } catch (e) {
        alert(this.$t("alert.error_page"))
        this.$router.go(-1)
      } finally {
        this.timeLoading = false
      }
    },

    openSelectConcept() {
      if (this.promotionPriceInfo && this.promotionPriceInfo.conceptId) {
        return
      }

      // Dialog를 띄우는 대신 화면 이동
      // this.show_concept_select = true
      // this.show_concept_select_componentKey++

      // productType에 따라 다른 화면으로 이동
      const routeName = this.productType === "premium" ? "castle-premium" : "castle-makershot"

      // URL query param의 event 정보를 함께 전달
      const query = {}
      if (this.$route.query.event) {
        query.event = this.$route.query.event
      }

      this.$router.push({
        name: routeName,
        query: query,
      })
    },

    openConceptPatternDialog() {
      if (!this.selected_concept) {
        return
      }
      this.show_concept_pattern_dialog = true
      this.show_concept_pattern_dialog_componentKey++
    },

    selectProduct(product, count) {
      console.log("selectProduct count", product, count)
      this.selectedProduct = product
      if (product.type === "group") {
        if (product.groupPricing) {
          const people = Number(count) || this.groupProductPersons[product.id] || 2
          quoteGroupProduct(product, people)
          this.persons = people
          this.groupProductPersons = { ...this.groupProductPersons, [product.id]: people }
        } else {
          this.persons = count
        }
        // group 타입 선택 시 '패턴 추가' 옵션 체크 해제
        this.selectedOptionProducts = this.selectedOptionProducts.filter((optionProduct) => {
          return !this.isPatternOption(optionProduct)
        })
      } else {
        this.persons = 1
      }
    },

    changeCount(value) {
      console.log("changeCount", value)
      if (this.selectedProduct.type === "group") {
        if (this.selectedProduct.groupPricing) {
          quoteGroupProduct(this.selectedProduct, Number(value))
          this.groupProductPersons = { ...this.groupProductPersons, [this.selectedProduct.id]: Number(value) }
        }
        this.persons = value
      } else {
        this.persons = 1
      }
    },

    getConceptTitle(concept) {
      switch (this.lang) {
        case "en":
          return concept.title_en || concept.title
        case "jp":
          return concept.title_jp || concept.title
        default:
          return concept.title
      }
    },

    getWorkDayTime(date) {
      // current_operation_time을 사용하여 운영시간 확인
      if (!this.current_operation_time) {
        return []
      }

      const weekData = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]
      const op_str = this.current_operation_time[weekData[date.day()]]

      return op_str.length
        ? op_str
            .split(",")
            .map((e) => e.trim())
            .filter((e) => e.length > 0)
            .sort()
        : []
    },

    WorkTimeCheck(date) {
      if (this.isHoliday(date)) {
        this.already_reserved_time_list = this.time_option_list
        return
      }

      if (this.isPremiumProduct) {
        // Premium 상품은 작가 스케줄 기준으로만 시간 제어 (휴일 제외)
        return
      }

      const workTimes = this.getWorkDayTime(date)
      if (workTimes.length) {
        const offTime = this.time_option_list.filter((item) => !workTimes.includes(item))
        this.already_reserved_time_list.push(...offTime)
        return
      }

      this.already_reserved_time_list = this.time_option_list
    },

    isHoliday(date) {
      return Holidays.includes(date.format("YYYY-MM-DD"))
    },

    // 오늘 날짜일시 예약시간이 지났는지 확인
    TodayTimePassCheck() {
      // 오늘 날짜일 경우
      if (this.dayjs().isSame(this.date, "date")) {
        this.time_option_list.forEach((time) => {
          // 30 분 이전 까지만 예약 가능 // 30 -> 1분으로 수정
          const now = this.dayjs().add(1, "minute")

          let timeToCheck = this.dayjs()
            .hour(parseInt(time.split(":")[0]))
            .minute(parseInt(time.split(":")[1]))

          // 현재 시각이 지났을 경우
          if (timeToCheck.isBefore(now)) {
            this.already_reserved_time_list.push(time)
          }
        })
      }     
    },

    isPastTime(time) {
      if (!this.dayjs().isSame(this.date, "date")) {
        return false
      }
      const [hour, minute] = time.split(":")
      if (hour === undefined || minute === undefined) {
        return false
      }
      const slot = this.dayjs().hour(parseInt(hour, 10)).minute(parseInt(minute, 10))
      const nowThreshold = this.dayjs().add(1, "minute")
      return slot.isBefore(nowThreshold)
    },
    // 오늘 날짜 예약 Full 시간대 check
    async todayTimeAvailableRoomCheck() {
      if (this.isPremiumProduct && this.selectedStaffId) {
        try {
          const slots = await this.getStaffScheduleSlots(this.date)
          const isPrincessBall = this.getPromotionCode === "PrincessBall"
          slots.forEach((slot) => {
            const blocked = isPrincessBall ? (slot.count || 0) >= this.getMaxAvailableRoom : !slot.available
            if (blocked && !this.already_reserved_time_list.includes(slot.time)) {
              this.already_reserved_time_list.push(slot.time)
            }
          })
        } catch (error) {
          console.error("premium availability check error", error)
          throw error
        }
        return
      }

      // 결제 전 기예약인원 재확인
      let reservedList = await this.$http
        .post("/api/reservation/select/specific/time/lafia/check", {
          params: {
            year: this.date.year(),
            month: this.date.month() + 1,
            day: this.date.date(),
          },
        })
        .then((res) => {
          return res.data.map((e) => ({
            time: e.time,
            count: Number(e.count),
          }))
        })

      for (let item of reservedList) {
        if (item.count >= this.getMaxAvailableRoom) {
          this.already_reserved_time_list.push(item.time)
        }
      }
    },

    async checkContinuouslyReservation() {
      const reservedList = await this.$http.post("/api/reservation/select/specific/time/lafia/check2", {
        params: {
          year: this.date.year(),
          month: this.date.month() + 1,
          day: this.date.date(),
        },
      })
      //console.log(reservedList)
      const myReservedLsit = reservedList.data.filter((e) => e.user_id === this.lafia_user.user_id)
      //console.log(myReservedLsit)

      // 오늘 날짜의 예약 시간대 리스트
      if (myReservedLsit.length > 0) {
        const timeList = myReservedLsit.map((e) => e.time)
        //console.log(timeList)

        timeList.forEach((reservedTime) => {
          const baseTime = this.dayjs(reservedTime, "HH:mm")

          this.time_option_list.forEach((operationTime) => {
            const opTime = this.dayjs(operationTime, "HH:mm")
            const diff = opTime.diff(baseTime, "minute")

            if (Math.abs(diff) <= 15) {
              // 예약된 시간과 15분 이내의 시간은 예약 불가능
              if (!this.already_reserved_time_list.includes(operationTime)) {
                this.already_reserved_time_list.push(operationTime)
              }
            }
          })
        })
      }
    },

    reservation: _.debounce(async function () {
      let orderId = Math.floor(Math.random() * 1000000000)

      if (!this.lafia_user.is_logined) {
        this.goLoginKeepingBooking()
        return
      }

      if (this.pay_loading) {
        alert(this.$t("alert.error_doing_pay_in_progress"))
        return
      }

      try {
        // 결제 진행중
        this.pay_loading = true

        // 오늘 날짜일시 예약시간이 지났는지 재확인
        await this.TodayTimePassCheck()

        // 중복 예약 체크
        const myReservedList = await this.$http.post("/api/reservation/select/specific/time/lafia/check/id", {
          params: {
            year: this.date.year(),
            month: this.date.month() + 1,
            day: this.date.date(),
            time: this.selected_time,
            user_id: this.lafia_user.user_id,
          },
        })

        if (myReservedList.data && myReservedList.data[0].count > 0) {
          alert(this.$t("alert.error_duplicate_reservation"))
          this.pay_loading = false // 결제 진행 중 취소
          this.$router.go(0)
          return
        }

        // 예약 전 다시한번 현재 시간대에 공간이 있는지 체크
        await this.todayTimeAvailableRoomCheck()

        // 시간 지났을 경우 종료
        if (this.already_reserved_time_list.includes(this.selected_time)) {
          alert("예약할 시각은 예약이 모두 완료되었습니다.")
          this.pay_loading = false // 결제 진행 중 취소
          this.$router.go(0)
          return
        }

        if (!this.selected_time) {
          alert("예약 시간을 다시 선택해주세요.")
          this.pay_loading = false
          return
        }

        if (!this.selectedProduct) {
          alert("예약할 상품을 선택해주세요.")
          this.pay_loading = false
          return
        }

        if (this.isPremiumProduct && !this.selectedStaffId) {
          alert("작가를 선택해주세요.")
          this.pay_loading = false
          return
        }

        const reservationDate = this.date ? this.date.format("YYYY-MM-DD") : null
        if (!reservationDate) {
          alert("예약 날짜를 다시 선택해주세요.")
          this.pay_loading = false
          return
        }

        const bookingBranchCode = this.bookingBranchCode
        if (!bookingBranchCode) {
          alert("예약 가능한 지점을 찾을 수 없습니다. 잠시 후 다시 시도해주세요.")
          this.pay_loading = false
          return
        }

        // 기본 상품 가격 계산
        let mainProductPrice = 0
        if (this.selectedProduct.type === "group") {
          mainProductPrice = Math.max(this.selectedUnitPrice * this.persons, 0)
        } else {
          mainProductPrice = Math.max(this.selectedUnitPrice, 0)
        }

        let orderList = [
          {
            productName: "라피아 이용권",
            orderNum: this.persons,
            price: this.selectedUnitPrice,
            totalPrice: this.persons * this.selectedUnitPrice,
            finalPrice: mainProductPrice,
            discountCode: this.promotionPriceInfo && this.getPromotionCode ? this.getPromotionCode : null,
          },
        ]

        // 선택된 옵션 상품들을 orderList에 추가
        this.selectedOptionProducts.forEach((optionProduct) => {
          const optionTitle = this.getOptionProductTitleKr(optionProduct)
          const quantity = optionProduct.quantity || 1
          const optionPrice = this.optionProductPrice(optionProduct)
          const itemTotalPrice = optionPrice * quantity
          orderList.push({
            productName: optionTitle || `옵션상품_${optionProduct.id}`,
            orderNum: quantity,
            price: optionPrice,
            totalPrice: itemTotalPrice,
            finalPrice: itemTotalPrice,
            discountCode: null,
          })
        })

        // memo에 전달할 상품 정보 생성
        const productMemoInfo = {
          id: this.selectedProduct.id,
          conceptId: this.selected_concept?.id || null,
          price: this.selectedUnitPrice,
          type: this.selectedProduct.type,
          freePicEditCount: this.selectedProduct.freePicEditCount,
          photoUsageConsent: this.selectedProduct.photoUsageConsent,
          photoUsageTermsVersion: this.selectedProduct.photoUsageTermsVersion,
          photoUsageAgreementAccepted:
            this.selectedProduct.photoUsageConsent === true &&
            this.$route.query.photoUse === "agree",
        }
        // 사진 사용에 동의한 예약에만 손님 한마디를 남긴다(프로모션 글에 사진과 함께 쓸 수 있는 말)
        const guestNote = this.guestNote.trim().slice(0, this.guestNoteMaxLength)
        if (guestNote && productMemoInfo.photoUsageAgreementAccepted) productMemoInfo.guestNote = guestNote

        const checkoutAmount = orderList.reduce((sum, item) => sum + item.finalPrice, 0)
        const checkoutName = this.getOrderName()
        const reservationPayload = {
          category: "라피아캐슬",
          productType: this.selected_concept?.productType || "makershot",
          reservationDate,
          reservationTime: this.selected_time,
          userId: this.lafia_user.user_id,
          conceptId: this.selected_concept?.id || null,
          concept: this.selected_concept?.title || "",
          cloth: this.persons,
          clothPrice: checkoutAmount,
          uncloth: 0,
          unclothPrice: 0,
          promotionCode: this.promotionPriceInfo && this.getPromotionCode ? this.getPromotionCode : null,
          individual: this.isPremiumProduct ? this.getPromotionCode : null,
        }
        if (this.isPremiumProduct) reservationPayload.staffUserId = this.selectedStaffId

        const orderRes = await this.$http.post("/api/order/v2/order", {
          orderId: orderId.toString(),
          userId: this.lafia_user.user_id,
          orderDetailList: orderList,
          memo: JSON.stringify(productMemoInfo),
          type: "온라인이용권",
          options: JSON.stringify(
            this.selectedOptionProducts.map((optionProduct) => ({
              id: optionProduct.id,
              code: optionProduct.code,
              title: this.getOptionProductTitleKr(optionProduct),
              price: this.optionProductPrice(optionProduct),
              quantity: optionProduct.quantity || 1,
            }))
          ),
          travelerCode: this.getTraveler,
        })

        if (Number(orderRes.data.amount) !== checkoutAmount) {
          throw new Error("주문 금액과 결제 금액이 일치하지 않습니다.")
        }
        reservationPayload.orderId = orderRes.data.id

        // 시간예약 입력하기

        await this.$http.post(`/api/booking/${bookingBranchCode}/reservations`, reservationPayload)

        if (checkoutAmount === 0) {
          this.$router.replace("/toss/success?orderId=" + orderId + "&paymentKey=free&amount=" + checkoutAmount)
        } else {
          const orderName = checkoutName

          if (this.isSupportAbroadPay && !this.isKRW) {
            // Payletter 결제: 현재 창에서 직접 결제 진행
            // 필수 파라미터 검증
            if (!this.lafia_user.user_id || !orderName) {
              console.error("Payletter 결제 요청 파라미터 누락:", {
                user_id: this.lafia_user.user_id,
                orderName: orderName,
              })
              alert("결제 정보가 불완전합니다. 다시 시도해주세요.")
              this.pay_loading = false
              return
            }

            try {
              // Payletter 결제 URL 요청
              const { online_url, mobile_url } = await payletterService.requestInternationalPay({
                storeorderno: orderId.toString(),
                amount: checkoutAmount,
                currency: "KRW", // "USD"
                payinfo: orderName,
                payeremail: getValidPayerEmail(this.lafia_user.email),
                payerid: this.lafia_user.user_id,
              })

              // 현재 창에서 직접 결제 URL로 이동
              // 결제 완료 후 payletter.ejs에서 /payletter/success로 리다이렉트됨
              // replace를 사용하여 히스토리에 남지 않도록 함 (뒤로가기 방지)
              if (this.$vuetify.breakpoint.mobile) {
                window.location.replace(mobile_url)
              } else {
                window.location.replace(online_url)
              }
            } catch (error) {
              console.error("Payletter 결제 요청 실패:", error)
              console.error("에러 상세:", error.response?.data)
              alert(error.response?.data?.error || "결제 요청에 실패했습니다. 다시 시도해주세요.")
              this.pay_loading = false
              return
            }
          } else {
            var tossPayments = TossPayments(process.env.VUE_APP_TOSS_PAYMENT_CLIENT_KEY)
            try {
              await tossPayments.requestPayment("카드", {
                amount: checkoutAmount,
                orderId: orderId,
                orderName: orderName,
                customerName: this.lafia_user.name,
                customerEmail: this.lafia_user.email,
                successUrl: window.location.origin + "/toss/success",
                failUrl: window.location.origin + "/fail",
                useEscrow: false,
              })
            } catch (e) {
              console.error("토스페이먼츠 결제", e)
              // 사용자 취소, 결제실패
              await this.cancelPayStatusUpdate({ order_id: orderRes.data.id, reason: "사용자취소" })
              this.pay_loading = false // 결제 진행 중 취소
            }
          }
        }
      } catch (e) {        
        const message = e?.response?.data?.message || this.$t("alert.error_page")
        alert(message)
      } finally {
        this.pay_loading = false
      }
    }, 500),

    getOrderName() {
      let orderName = this.getProductTitle(this.selectedProduct)
      if (this.selectedProduct.type === "group") {
        const personText = this.lang === "ko" ? "명" : "people"
        orderName += " " + this.persons + personText
      }

      // 선택된 옵션 상품들의 이름 추가
      if (this.selectedOptionProducts.length > 0) {
        const optionNames = this.selectedOptionProducts
          .map((optionProduct) => {
            return this.getOptionProductTitleKr(optionProduct)
          })
          .filter((name) => name) // 빈 문자열 제거

        if (optionNames.length > 0) {
          orderName += " + " + optionNames.join(" + ")
        }
      }

      return orderName
    },

    setDescription() {
      let key = "content"

      this.desc_options.content = this.default_price[key]
      if (this.lang === "en" || this.lang === "jp") {
        let langkey = key + `_${this.lang}`
        if (this.default_price[langkey]) {
          this.desc_options.content = this.default_price[langkey]
        }
      }

      if (this.promotionPriceInfo && this.promotionPriceInfo[key]) {
        this.desc_options.content = this.promotionPriceInfo[key]
        if (this.lang === "en" || this.lang === "jp") {
          let langkey = key + `_${this.lang}`
          if (this.promotionPriceInfo[langkey]) {
            this.desc_options.content = this.promotionPriceInfo[langkey]
          }
        }
      }

      this.descComponentKey++
    },

    checkPromotionDate(promotion) {
      console.log("checkPromotionDate", promotion)
      // 해당 패키지가 예약 가능 아닌지 체크가 필요함.
      const startAt = this.dayjs(promotion.startAt)
      const endAt = this.dayjs(promotion.endAt)

      if (!this.date.isBetween(startAt, endAt, "day", "[]")) {
        // 예약 기간이 아닌 경우 — 양쪽 모두 예약 불가 상태이므로 시간 리스트 비움
        this.available_time_list = []
        this.already_reserved_time_list = []
        this.selected_time = ""

        if (this.dayjs().isAfter(endAt, "day")) {
          // 예약 가능한 날짜가 모두 과거인 경우 — 안내 후 홈으로 이동
          this.showWarningDialog("dialog.promotion-warning-end-guide")
        }
        // 시작 전이거나 범위 밖이면 시간 리스트만 비운 채로 사용자에게 다른 날짜 선택을 유도
        // this.showWarningDialog(this.$t("dialog.promotion-warning-date-guide", { startAt: startAt.format("YYYY/MM/DD"), endAt: endAt.format("YYYY/MM/DD") }))
      }
    },

    showWarningDialog(message) {
      this.warning_dialog_messsage = message
      this.show_warning_dialog = true
      this.show_warning_dialog_componentKey++
    },

    confirmDialog() {
      this.show_warning_dialog = false
      if (this.warning_dialog_messsage === "dialog.promotion-warning-end-guide") {
        this.$router.replace("/")
      } else {
        const product = this.promotionPriceInfo
        if (product) {
          const startAt = this.dayjs(product.startAt).startOf("day")
          if (this.dayjs().isBefore(startAt)) {
            this.date = startAt.startOf("day")
          } else {
            this.date = this.dayjs().startOf("day")
          }
          this.dateSelectComponentKey++
        }
      }
      this.warning_dialog_messsage = ""
      this.show_warning_dialog_componentKey++
    },

    warningDialogInputEvent(e) {
      !e && this.confirmDialog()
    },

    showRefundPolicyDialog() {
      // 로그인을 먼저 체크
      if (!this.lafia_user.is_logined) {
        this.goLoginKeepingBooking()
        return
      }

      this.refund_policy_dialog_componentKey++
      this.show_refund_policy = true
    },

    agreeRefundPolicy() {
      this.show_refund_policy = false
      this.reservation()
    },

    // 옵션 상품 로드
    async loadOptionProducts() {
      try {
        // promotionPriceInfo에서 option_products 가져오기
        const optionProductIds = this.promotionPriceInfo?.option_products

        if (optionProductIds) {
          // ID 문자열로 옵션 상품 정보 조회
          this.optionProducts = await optionProductService.getOptionProductsByIdString(optionProductIds)
        } else {
          this.optionProducts = []
        }
      } catch (error) {
        console.error("옵션 상품 로드 실패:", error)
        this.optionProducts = []
      }
    },

    // 옵션 상품 선택 여부 확인
    isOptionProductSelected(productId) {
      return this.selectedOptionProducts.some((p) => p.id === productId)
    },

    optionProductPrice(product) {
      return optionUnitPrice(product, this.selectedProduct && this.selectedProduct.type, this.persons)
    },

    // 패턴 추가 옵션인지 확인
    isPatternOption(product) {
      if (!product || !product.title) return false

      let title = ""
      if (typeof product.title === "string") {
        try {
          const parsed = JSON.parse(product.title)
          title = parsed.kr || product.title
        } catch {
          title = product.title
        }
      } else if (typeof product.title === "object") {
        title = product.title.kr || ""
      }

      return title.includes("패턴 추가")
    },

    // 패턴 개수 가져오기
    getPatternCount() {
      if (this.selected_concept && Array.isArray(this.selected_concept.pattern_others_image)) {
        return this.selected_concept.pattern_others_image.length
      }
      return 0
    },

    // 옵션 상품 선택/해제 처리
    onOptionProductChange({ product, selected, quantity = 1 }) {
      if (selected) {
        // 선택 추가 또는 수량 업데이트
        const existingIndex = this.selectedOptionProducts.findIndex((p) => p.id === product.id)
        if (existingIndex > -1) {
          // 이미 선택된 상품이면 수량 업데이트
          this.$set(this.selectedOptionProducts, existingIndex, {
            ...product,
            quantity: quantity,
          })
        } else {
          // 새로 추가
          this.selectedOptionProducts.push({
            ...product,
            quantity: quantity,
          })
        }
      } else {
        // 선택 제거
        const index = this.selectedOptionProducts.findIndex((p) => p.id === product.id)
        if (index > -1) {
          this.selectedOptionProducts.splice(index, 1)
        }
      }
    },

    // 옵션 상품의 제목 가져오기
    getOptionProductTitle(product) {
      if (!product || !product.title) return ""

      // title이 문자열인 경우 JSON 파싱 시도
      if (typeof product.title === "string") {
        try {
          const parsed = JSON.parse(product.title)
          switch (this.lang) {
            case "en":
              return parsed.en || parsed.kr || ""
            case "jp":
              return parsed.jp || parsed.kr || ""
            default:
              return parsed.kr || ""
          }
        } catch {
          return product.title
        }
      }

      // title이 객체인 경우
      if (typeof product.title === "object") {
        switch (this.lang) {
          case "en":
            return product.title.en || product.title.kr || ""
          case "jp":
            return product.title.jp || product.title.kr || ""
          default:
            return product.title.kr || ""
        }
      }

      return ""
    },

    // 옵션 상품의 한글 제목만 가져오기
    getOptionProductTitleKr(product) {
      if (!product || !product.title) return ""

      let title = ""

      // title이 문자열인 경우 JSON 파싱 시도
      if (typeof product.title === "string") {
        try {
          const parsed = JSON.parse(product.title)
          title = parsed.kr || ""
        } catch {
          title = product.title
        }
      } else if (typeof product.title === "object") {
        // title이 객체인 경우
        title = product.title.kr || ""
      }

      // 수량이 2개 이상이면 수량 정보 추가
      const quantity = product.quantity || 1
      if (quantity > 1) {
        title += ` x ${quantity}`
      }

      return title
    },
  },
}
</script>
<style scoped>
.timeSelectBtn {
  border: 1px solid lightgray;
  color: #515151;
  font-size: 16px;
  font-weight: normal;
  margin: 0 3px;
  background-color: white !important;
}

.enable {
  background: #ef7fab !important;
  color: white !important;
}

.dateSelectBox {
  background-color: #fff;
  border-radius: 8px;
  padding: 12px 0px;
}

.booking-step-panel {
  background-color: #fdecee;
  border-radius: 8px;
  padding: 16px 8px 16px;
}

.booking-step-panel__header {
  margin-bottom: 24px;
}

.borderBox {
  border-radius: 10px;
  border: 4px solid #f5e4e4;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 10px;
  padding-right: 10px;
}

::v-deep .conceptBtn.v-btn--disabled.v-btn--has-bg {
  cursor: not-allowed !important; /* 커서 스타일 */
  background-color: #ef7fab !important;
  color: white !important;
}

.conceptTitleBtn {
  border: 4px solid #f5e4e4 !important;
  background-color: white !important;
  color: #545454;
  border-radius: 1.5rem;
  font-size: 15px;
  font-weight: 400;
}

::v-deep .conceptTitleBtn.v-btn--disabled.v-btn--has-bg {
  cursor: not-allowed !important; /* 커서 스타일 */
  color: #545454 !important;
  background-color: white !important;
}

.stepTitle {
  border-bottom: 1px solid #c2bcba;
}

.conceptImageArea {
  border: 3px solid #f5e4e4;
  border-radius: 8px;
  width: 180px;
  height: 216px;
  cursor: pointer;
  color: #8d8d8d;
  display: flex;
  justify-content: center;
  align-items: center;
}

.conceptImageArea span {
  color: #8d8d8d;
  font-size: 14px;
}

.conceptImageArea:disabled {
  cursor: not-allowed;
}

.conceptImageAreaBorder {
  border: 3px solid #f5e4e4;
  border-radius: 8px;
}
.imageSelectable {
  cursor: pointer;
}

.imageNonSelectable {
  cursor: not-allowed;
}
</style>
